import React, { useState, useEffect } from 'react';
import { getAdminToken, setAdminToken, clearAdminToken } from '../utils/adminAuth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminLoginPage = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      const data = await res.json();
      if (res.ok && data.success) { setAdminToken(data.token); onLogin(data.token); }
      else { setError(data.detail || 'Mot de passe incorrect'); }
    } catch { setError('Erreur de connexion au serveur'); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F1F5F9' }}>
      <div style={{ backgroundColor: 'white', padding: '40px', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', width: '100%', maxWidth: '400px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#1E3A5F', marginBottom: '10px', textAlign: 'center' }}>Administration</h1>
        <p style={{ color: '#64748B', fontSize: '14px', textAlign: 'center', marginBottom: '30px' }}>Atlantique Spa Boutique</p>
        <form onSubmit={handleSubmit} autoComplete="on">
          <input type="text" name="username" autoComplete="username" value="admin" readOnly
            style={{ position: 'absolute', opacity: 0, height: 0, width: 0, overflow: 'hidden' }} tabIndex={-1} />
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: '#475569', fontSize: '14px', fontWeight: '500' }}>Mot de passe</label>
            <input type="password" name="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input"
              style={{ width: '100%', padding: '12px 16px', border: '1px solid #E2E8F0', borderRadius: '8px', fontSize: '16px', outline: 'none', boxSizing: 'border-box' }}
              placeholder="Entrez le mot de passe" autoFocus />
          </div>
          {error && <div data-testid="login-error" style={{ padding: '12px 16px', backgroundColor: '#FEE2E2', color: '#991B1B', borderRadius: '8px', fontSize: '14px', marginBottom: '15px' }}>{error}</div>}
          <button type="submit" disabled={loading} data-testid="login-submit-btn"
            style={{ width: '100%', padding: '14px', backgroundColor: loading ? '#94A3B8' : '#1E3A5F', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer' }}>
            {loading ? 'Connexion...' : 'Se connecter'}
          </button>
        </form>
      </div>
    </div>
  );
};

const AdminNav = ({ active }) => (
  <div style={{ display: 'flex', marginBottom: '24px', backgroundColor: 'white', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
    {[
      { key: 'dashboard', label: 'Dashboard', href: '/admin/dashboard' },
      { key: 'commandes', label: 'Commandes', href: '/admin/commandes' },
      { key: 'clients', label: 'Clients', href: '/admin/clients' },
      { key: 'factures', label: 'Factures', href: '/admin/factures' }
    ].map((tab, i) => (
      <a key={tab.key} href={tab.href} data-testid={`nav-${tab.key}`}
        style={{
          flex: 1, padding: '14px 20px', textAlign: 'center', fontSize: '14px', fontWeight: '600',
          color: active === tab.key ? 'white' : '#475569',
          backgroundColor: active === tab.key ? '#1E3A5F' : 'white',
          textDecoration: 'none',
          borderRight: i < 3 ? '1px solid #E2E8F0' : 'none'
        }}>
        {tab.label}
      </a>
    ))}
  </div>
);

const AdminCommandesPage = () => {
  const [token, setToken] = useState(null);
  const [isVerifying, setIsVerifying] = useState(true);
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showShipModal, setShowShipModal] = useState(false);
  const [shipmentData, setShipmentData] = useState({ carrier_name: 'Colissimo', tracking_number: '', tracking_url: '' });
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);

  useEffect(() => {
    const verify = async () => {
      const t = getAdminToken();
      if (!t) { setIsVerifying(false); return; }
      try {
        const res = await fetch(`${API_URL}/api/admin/verify`, { headers: { 'Authorization': `Bearer ${t}` } });
        const d = await res.json();
        if (d.valid) setToken(t); else clearAdminToken();
      } catch { clearAdminToken(); }
      setIsVerifying(false);
    };
    verify();
  }, []);

  useEffect(() => {
    if (token) fetchOrders();
  }, [token]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/payments/orders`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.status === 401) { handleLogout(); return; }
      if (res.ok) {
        const data = await res.json();
        data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setOrders(data);
      }
    } catch (e) { console.error('Error:', e); }
    finally { setLoading(false); }
  };

  const handleLogin = (t) => setToken(t);
  const handleLogout = async () => {
    try { await fetch(`${API_URL}/api/admin/logout`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } }); } catch (e) { console.error('Logout error:', e); }
    clearAdminToken(); setToken(null); setOrders([]); setSelectedOrder(null);
  };

  const handleDownloadInvoice = async (invoiceNumber) => {
    try {
      const res = await fetch(`${API_URL}/api/invoices/${invoiceNumber}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `facture-${invoiceNumber}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (e) { console.error('Download error:', e); }
  };

  const handleShipOrder = async () => {
    if (!shipmentData.tracking_number) { setMessage({ type: 'error', text: 'Veuillez entrer un numero de suivi' }); return; }
    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/payments/orders/${selectedOrder.order_number}/ship`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(shipmentData)
      });
      if (res.status === 401) { handleLogout(); return; }
      const result = await res.json();
      if (res.ok) {
        setMessage({ type: 'success', text: `Commande expediee ! Email ${result.email_sent ? 'envoye' : 'non envoye'} au client.` });
        setShowShipModal(false);
        setSelectedOrder({ ...selectedOrder, status: 'shipped', carrier_name: shipmentData.carrier_name, tracking_number: shipmentData.tracking_number, tracking_url: result.tracking_url });
        fetchOrders();
      } else { setMessage({ type: 'error', text: result.detail || 'Erreur' }); }
    } catch { setMessage({ type: 'error', text: 'Erreur de connexion' }); }
    finally { setProcessing(false); }
  };

  const getStatusBadge = (status) => {
    const styles = {
      paid: { bg: '#FEF3C7', color: '#92400E', text: 'Payee' },
      processing: { bg: '#DBEAFE', color: '#1E40AF', text: 'En preparation' },
      shipped: { bg: '#D1FAE5', color: '#065F46', text: 'Expediee' },
      delivered: { bg: '#E0E7FF', color: '#3730A3', text: 'Livree' },
    };
    const s = styles[status] || { bg: '#F1F5F9', color: '#475569', text: status };
    return <span data-testid="order-status-badge" style={{ display: 'inline-block', padding: '4px 12px', backgroundColor: s.bg, color: s.color, borderRadius: '20px', fontSize: '12px', fontWeight: '600' }}>{s.text}</span>;
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const formatPrice = (p) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(p);

  if (isVerifying) return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F1F5F9' }}><p style={{ color: '#64748B' }}>Verification...</p></div>;
  if (!token) return <AdminLoginPage onLogin={handleLogin} />;

  // Order Detail View
  if (selectedOrder) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#F1F5F9', padding: '20px' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <button onClick={() => { setSelectedOrder(null); setMessage(null); }} data-testid="back-to-list-btn"
              style={{ padding: '10px 20px', backgroundColor: 'white', border: '1px solid #E2E8F0', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>
              ← Retour a la liste
            </button>
          </div>

          {message && (
            <div style={{ padding: '15px 20px', backgroundColor: message.type === 'success' ? '#D1FAE5' : '#FEE2E2', color: message.type === 'success' ? '#065F46' : '#991B1B', borderRadius: '8px', marginBottom: '20px', fontSize: '14px' }}>
              {message.text}
            </div>
          )}

          <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ padding: '25px 30px', borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#0F172A', margin: 0 }}>Commande #{selectedOrder.order_number}</h1>
                <p style={{ color: '#64748B', fontSize: '14px', margin: '5px 0 0 0' }}>{formatDate(selectedOrder.created_at)}</p>
              </div>
              {getStatusBadge(selectedOrder.status)}
            </div>

            <div style={{ padding: '30px' }}>
              {/* Customer Info */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#0F172A', marginBottom: '15px' }}>Informations client</h3>
                <div style={{ backgroundColor: '#F8FAFC', padding: '20px', borderRadius: '8px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div>
                    <p style={{ color: '#64748B', fontSize: '12px', margin: '0 0 4px 0' }}>Nom</p>
                    <p data-testid="detail-customer-name" style={{ color: '#0F172A', fontSize: '14px', margin: 0, fontWeight: '500' }}>{selectedOrder.customer_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p style={{ color: '#64748B', fontSize: '12px', margin: '0 0 4px 0' }}>Email</p>
                    <p data-testid="detail-customer-email" style={{ color: '#0F172A', fontSize: '14px', margin: 0, fontWeight: '500' }}>{selectedOrder.customer_email || 'N/A'}</p>
                  </div>
                  <div>
                    <p style={{ color: '#64748B', fontSize: '12px', margin: '0 0 4px 0' }}>Telephone</p>
                    <p data-testid="detail-customer-phone" style={{ color: '#0F172A', fontSize: '14px', margin: 0, fontWeight: '500' }}>{selectedOrder.customer_phone || 'N/A'}</p>
                  </div>
                  <div>
                    <p style={{ color: '#64748B', fontSize: '12px', margin: '0 0 4px 0' }}>Moyen de paiement</p>
                    <p data-testid="detail-payment-method" style={{ color: '#0F172A', fontSize: '14px', margin: 0, fontWeight: '500' }}>{selectedOrder.payment_method || 'Carte bancaire'}</p>
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              {selectedOrder.shipping_address && (
                <div style={{ marginBottom: '30px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#0F172A', marginBottom: '15px' }}>Adresse de livraison</h3>
                  <div style={{ backgroundColor: '#F0FDF4', padding: '20px', borderRadius: '8px' }}>
                    <p style={{ color: '#166534', fontSize: '14px', lineHeight: '1.8', margin: 0 }}>
                      {selectedOrder.shipping_address.name}<br />
                      {selectedOrder.shipping_address.line1}<br />
                      {selectedOrder.shipping_address.line2 && <>{selectedOrder.shipping_address.line2}<br /></>}
                      {selectedOrder.shipping_address.postal_code} {selectedOrder.shipping_address.city}<br />
                      {selectedOrder.shipping_address.country === 'FR' ? 'France' : selectedOrder.shipping_address.country}
                    </p>
                  </div>
                </div>
              )}

              {/* Products */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#0F172A', marginBottom: '15px' }}>Produits commandes</h3>
                <div style={{ backgroundColor: '#F8FAFC', borderRadius: '8px', overflow: 'hidden' }}>
                  {selectedOrder.items?.map((item, index) => (
                    <div key={index} style={{ padding: '15px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: index < selectedOrder.items.length - 1 ? '1px solid #E2E8F0' : 'none' }}>
                      <div>
                        <p style={{ margin: 0, fontWeight: '500', color: '#0F172A', fontSize: '14px' }}>{item.name}</p>
                        <p style={{ margin: '4px 0 0 0', color: '#64748B', fontSize: '13px' }}>Quantite : {item.quantity}</p>
                      </div>
                      <p style={{ margin: 0, fontWeight: '600', color: '#0F172A', fontSize: '14px' }}>{formatPrice(item.price * item.quantity)}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Total */}
              <div style={{ backgroundColor: '#1E3A5F', padding: '20px 25px', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                <span style={{ color: 'white', fontSize: '16px' }}>Total paye</span>
                <span data-testid="detail-total" style={{ color: 'white', fontSize: '24px', fontWeight: '700' }}>{formatPrice(selectedOrder.total_amount)}</span>
              </div>

              {/* Invoice */}
              {selectedOrder.invoice_number && (
                <div style={{ backgroundColor: '#EFF6FF', padding: '16px 20px', borderRadius: '8px', marginBottom: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <p style={{ color: '#1E40AF', fontSize: '14px', fontWeight: '600', margin: 0 }}>Facture {selectedOrder.invoice_number}</p>
                    <p style={{ color: '#64748B', fontSize: '12px', margin: '3px 0 0' }}>Facture acquittee</p>
                  </div>
                  <button onClick={() => handleDownloadInvoice(selectedOrder.invoice_number)} data-testid="download-invoice-btn"
                    style={{ padding: '8px 16px', backgroundColor: '#1E3A5F', color: 'white', border: 'none', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', fontWeight: '600' }}>
                    Telecharger PDF
                  </button>
                </div>
              )}

              {/* Tracking */}
              {selectedOrder.status === 'shipped' && selectedOrder.tracking_number && (
                <div style={{ backgroundColor: '#D1FAE5', padding: '20px', borderRadius: '8px', marginBottom: '30px' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#065F46', marginBottom: '10px' }}>Informations d'expedition</h3>
                  <p style={{ color: '#065F46', fontSize: '14px', margin: '5px 0' }}><strong>Transporteur :</strong> {selectedOrder.carrier_name}</p>
                  <p style={{ color: '#065F46', fontSize: '14px', margin: '5px 0' }}><strong>N de suivi :</strong> {selectedOrder.tracking_number}</p>
                  {selectedOrder.tracking_url && (
                    <a href={selectedOrder.tracking_url} target="_blank" rel="noopener noreferrer" style={{ color: '#065F46', fontSize: '14px' }}>Suivre le colis →</a>
                  )}
                </div>
              )}

              {/* Ship Button */}
              {selectedOrder.status === 'paid' && (
                <button onClick={() => { setShipmentData({ carrier_name: 'Colissimo', tracking_number: '', tracking_url: '' }); setShowShipModal(true); }}
                  data-testid="mark-shipped-btn"
                  style={{ width: '100%', padding: '16px', backgroundColor: '#10B981', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: 'pointer' }}>
                  Marquer comme expediee
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Ship Modal */}
        {showShipModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '30px', width: '100%', maxWidth: '450px', margin: '20px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#0F172A', marginBottom: '25px' }}>Informations d'expedition</h2>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#475569', fontSize: '14px' }}>Transporteur</label>
                <select value={shipmentData.carrier_name} onChange={(e) => setShipmentData({ ...shipmentData, carrier_name: e.target.value })}
                  data-testid="carrier-select"
                  style={{ width: '100%', padding: '12px', border: '1px solid #E2E8F0', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}>
                  <option value="Colissimo">Colissimo</option>
                  <option value="Chronopost">Chronopost</option>
                  <option value="UPS">UPS</option>
                  <option value="DHL">DHL</option>
                  <option value="Mondial Relay">Mondial Relay</option>
                  <option value="Autre">Autre</option>
                </select>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#475569', fontSize: '14px' }}>Numero de suivi *</label>
                <input type="text" value={shipmentData.tracking_number} onChange={(e) => setShipmentData({ ...shipmentData, tracking_number: e.target.value })}
                  data-testid="tracking-number-input"
                  style={{ width: '100%', padding: '12px', border: '1px solid #E2E8F0', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                  placeholder="Ex: 1234567890" />
              </div>
              <div style={{ marginBottom: '25px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#475569', fontSize: '14px' }}>Lien de suivi (optionnel)</label>
                <input type="url" value={shipmentData.tracking_url} onChange={(e) => setShipmentData({ ...shipmentData, tracking_url: e.target.value })}
                  data-testid="tracking-url-input"
                  style={{ width: '100%', padding: '12px', border: '1px solid #E2E8F0', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                  placeholder="https://..." />
                <p style={{ color: '#64748B', fontSize: '12px', marginTop: '5px' }}>Laissez vide pour generer automatiquement</p>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => setShowShipModal(false)}
                  style={{ flex: 1, padding: '14px', backgroundColor: 'white', color: '#475569', border: '1px solid #E2E8F0', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' }}>
                  Annuler
                </button>
                <button onClick={handleShipOrder} disabled={processing} data-testid="confirm-ship-btn"
                  style={{ flex: 1, padding: '14px', backgroundColor: processing ? '#94A3B8' : '#10B981', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: processing ? 'not-allowed' : 'pointer' }}>
                  {processing ? 'Envoi...' : 'Confirmer l\'expedition'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Orders List View
  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#F1F5F9', padding: '20px' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <AdminNav active="commandes" />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
          <div>
            <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#0F172A', margin: 0 }}>Commandes</h1>
            <p style={{ color: '#64748B', fontSize: '14px', margin: '5px 0 0 0' }}>Gestion des commandes Atlantique Spa Boutique</p>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={async () => {
              if (!window.confirm('Cela va nettoyer toutes les donnees test et re-synchroniser depuis Stripe. Continuer ?')) return;
              try {
                setSyncLoading(true);
                const res = await fetch(`${API_URL}/api/admin/sync-stripe`, {
                  method: 'POST',
                  headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await res.json();
                if (res.ok) {
                  const msg = `Synchronisation terminee!\n\nNettoyage: ${data.cleanup.orders_deleted} commandes, ${data.cleanup.customers_deleted} clients, ${data.cleanup.invoices_deleted} factures supprimees\n\nRecreation: ${data.sync.orders_created} commandes depuis Stripe\n\nEtat final: ${data.final_state.orders} commandes, ${data.final_state.customers} clients, ${data.final_state.invoices} factures`;
                  alert(msg);
                  fetchOrders();
                } else {
                  alert(`Erreur: ${data.detail || 'Erreur de synchronisation'}`);
                }
              } catch (e) { alert('Erreur de connexion'); }
              finally { setSyncLoading(false); }
            }} disabled={syncLoading} data-testid="sync-stripe-btn"
              style={{ padding: '10px 20px', backgroundColor: '#0EA5E9', color: 'white', border: 'none', borderRadius: '8px', cursor: syncLoading ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '600', opacity: syncLoading ? 0.6 : 1 }}>
              {syncLoading ? 'Synchronisation...' : 'Sync Stripe'}
            </button>
            <button onClick={handleLogout} data-testid="logout-btn"
              style={{ padding: '10px 20px', backgroundColor: 'white', border: '1px solid #E2E8F0', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', color: '#64748B' }}>
              Deconnexion
            </button>
          </div>
        </div>

        <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: '60px', textAlign: 'center', color: '#64748B' }}>Chargement des commandes...</div>
          ) : orders.length === 0 ? (
            <div style={{ padding: '60px', textAlign: 'center', color: '#64748B' }}>Aucune commande pour le moment</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }} data-testid="orders-table">
                <thead>
                  <tr style={{ backgroundColor: '#F8FAFC' }}>
                    <th style={thStyle}>Commande</th>
                    <th style={thStyle}>Client</th>
                    <th style={thStyle}>Telephone</th>
                    <th style={thStyle}>Montant</th>
                    <th style={thStyle}>Date</th>
                    <th style={thStyle}>Statut</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order, index) => (
                    <tr key={order.id || index} style={{ borderBottom: '1px solid #E2E8F0' }} data-testid={`order-row-${index}`}>
                      <td style={tdStyle}><span style={{ fontWeight: '600', color: '#0F172A', fontSize: '14px' }}>#{order.order_number}</span></td>
                      <td style={tdStyle}>
                        <p style={{ margin: 0, fontWeight: '500', color: '#0F172A', fontSize: '14px' }}>{order.customer_name || 'N/A'}</p>
                        <p style={{ margin: '2px 0 0 0', color: '#64748B', fontSize: '13px' }}>{order.customer_email}</p>
                      </td>
                      <td style={tdStyle}><span style={{ color: '#475569', fontSize: '13px' }}>{order.customer_phone || '-'}</span></td>
                      <td style={tdStyle}><span style={{ fontWeight: '600', color: '#0F172A', fontSize: '14px' }}>{formatPrice(order.total_amount)}</span></td>
                      <td style={{ ...tdStyle, color: '#64748B', fontSize: '14px' }}>{formatDate(order.created_at)}</td>
                      <td style={tdStyle}>{getStatusBadge(order.status)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <button onClick={() => { setSelectedOrder(order); setMessage(null); }} data-testid={`view-order-btn-${index}`}
                          style={{ padding: '8px 16px', backgroundColor: '#1E3A5F', color: 'white', border: 'none', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}>
                          Voir
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const thStyle = { padding: '15px 20px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748B', textTransform: 'uppercase' };
const tdStyle = { padding: '18px 20px' };

export default AdminCommandesPage;
